import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Button,
  Divider,
  Grid,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import WarningAmber from '@mui/icons-material/WarningAmber';
import ContentCopy from '@mui/icons-material/ContentCopy';
import OpenInNew from '@mui/icons-material/OpenInNew';
import api from '../../services/api';
import { listBankAccounts, formatAccountNumber, type BankAccount } from '../../services/bankAccountService';

const WL_URL = 'https://podatki-arch.mf.gov.pl/wykaz-podatnikow-vat-wyszukiwarka/';

export interface SettleInvoiceData {
  id: number | string;
  document_type: string;
  created_date: string;
  received_date?: string;
  document_date?: string;
  due_date: string;
  paid_date?: string | null;
  project_code: string;
  contractor: string;
  contractor_address?: string | null;
  contractor_postal_city?: string | null;
  nip?: string | null;
  invoice_number?: string | null;
  ksef_number?: string | null;
  gross_amount: number;
  net_amount: number;
  vat_rate: string;
  account_number?: string | null;
  warehouse?: string | null;
  google_drive_file_id?: string | null;
  google_drive_file_url?: string | null;
}

interface SettleInvoiceDrawerProps {
  open: boolean;
  onClose: () => void;
  invoiceData: SettleInvoiceData | null;
  onSettle: (id: number | string, data: {
    paid_date: string;
    payment_method?: string;
    currency?: string;
    amount_to_pay?: number;
    amount_vat?: number;
    amount_from_vat_account?: number;
    transfer_title?: string;
    unitresco_account?: string;
  }) => Promise<void>;
  viewMode?: boolean;
  onUnsettle?: (id: number | string) => Promise<void>;
}

// Style pól formularza
const textFieldSx = {
  '& .MuiInputLabel-root': {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '16px',
    color: '#2D2D32'
  },
  '& .MuiOutlinedInput-root': {
    height: 36,
    backgroundColor: '#fff',
    borderRadius: '4px'
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
    boxSizing: 'border-box',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    color: '#2D2D32'
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: '#2D2D32',
    color: '#2D2D32',
  },
  '& .MuiFormHelperText-root': { ml: 0 }
} as const;

const readOnlyFieldSx = {
  ...textFieldSx,
  '& .MuiOutlinedInput-root': {
    ...textFieldSx['& .MuiOutlinedInput-root'],
    backgroundColor: '#F5F5F8',
  },
} as const;

const selectSx = {
  height: 36,
  backgroundColor: '#fff',
  borderRadius: '4px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
  '& .MuiSelect-select': {
    padding: '8px 12px',
    boxSizing: 'border-box',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    color: '#2D2D32'
  }
} as const;

const tabSx = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  textTransform: 'none' as const,
  color: '#020242',
  minWidth: 'auto',
  maxWidth: 'none',
  px: 0,
  textAlign: 'left' as const,
  '&.Mui-selected': { fontWeight: 500, color: '#020242' },
};

const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '18px', color: '#595966', mb: '4px' }}>
    {text}
    {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
  </Typography>
);

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <Box sx={{ mb: 1.5, mt: 0.5 }}>
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '14px', color: '#020242' }}>
      {title}
    </Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Box>
);

const formatMoney = (v: number) => v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';

const SettleInvoiceDrawer: React.FC<SettleInvoiceDrawerProps> = ({ open, onClose, invoiceData, onSettle, viewMode = false, onUnsettle }) => {
  // Zakładki
  const [tabValue, setTabValue] = useState(0);

  // Formularz
  const [paymentMethod, setPaymentMethod] = useState<string>('przelew');
  const [settleDate, setSettleDate] = useState<string>('');
  const [currency, setCurrency] = useState<string>('PLN');
  const [amountToPay, setAmountToPay] = useState<string>('');
  const [amountVat, setAmountVat] = useState<string>('');
  const [amountFromVatAccount, setAmountFromVatAccount] = useState<string>('0.00');
  const [transferTitle, setTransferTitle] = useState<string>('');
  const [unitrescoAccount, setUnitrescoAccount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [companyBankAccounts, setCompanyBankAccounts] = useState<BankAccount[]>([]);

  // Biała lista VAT
  const [whiteListStatus, setWhiteListStatus] = useState<'checking' | 'valid' | 'invalid' | 'error' | null>(null);

  // Skrót klawiszowy: § – przełączanie zakładek
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const isShortcut = e.code === 'IntlBackslash' || e.key === '§' || e.key === '£' || e.key === '±';
      if (!isShortcut) return;
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (e.target as any)?.isContentEditable) return;
      e.preventDefault();
      e.stopPropagation();
      const step = e.shiftKey ? -1 : 1;
      setTabValue(v => (v + step + 2) % 2);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  // Resetuj formularz przy otwarciu
  useEffect(() => {
    if (open && invoiceData) {
      setTabValue(0);
      const today = new Date().toISOString().slice(0, 10);
      setSettleDate(today);
      setCurrency('PLN');
      setAmountToPay(String(Number(invoiceData.gross_amount || 0).toFixed(2)));
      const gross = Number(invoiceData.gross_amount || 0);
      const net = Number(invoiceData.net_amount || 0);
      const vat = gross - net;
      setAmountVat(vat > 0 ? vat.toFixed(2) : '0.00');
      setPaymentMethod(invoiceData.document_type === 'Gotówka' ? 'gotówka' : 'przelew PP');
      if (invoiceData.document_type !== 'Gotówka') {
        setAmountFromVatAccount(vat > 0 ? vat.toFixed(2) : '0.00');
      } else {
        setAmountFromVatAccount('0.00');
      }
      setTransferTitle(`Zapłata za fakturę ${invoiceData.invoice_number || ''}`);
      setUnitrescoAccount('');
      setWhiteListStatus(null);
      // Załaduj rachunki bankowe i ustaw domyślny (główny)
      listBankAccounts().then((accounts) => {
        setCompanyBankAccounts(accounts);
        const primary = accounts.find((a) => a.is_primary);
        if (primary) {
          setUnitrescoAccount(formatAccountNumber(primary.account_number));
        }
      }).catch(() => {});
    }
  }, [open, invoiceData]);

  // Automatyczne sprawdzenie białej listy VAT przy otwarciu
  useEffect(() => {
    if (!open || !invoiceData) return;
    const nip = invoiceData.nip?.replace(/[^0-9]/g, '') || '';
    const account = invoiceData.account_number?.replace(/[^0-9]/g, '') || '';
    if (nip.length !== 10 || account.length < 26) {
      if (nip.length > 0 && account.length > 0) {
        setWhiteListStatus('error');
      }
      return;
    }
    let cancelled = false;
    setWhiteListStatus('checking');
    const today = new Date().toISOString().slice(0, 10);
    api.get('/white-list/check', { params: { nip, bankAccount: account, date: today } })
      .then((res) => {
        if (cancelled) return;
        const result = res.data?.result;
        const assigned = result?.accountAssigned;
        if (assigned === 'TAK' || assigned === 'VIRTUAL_MATCH' || assigned === 'FOREIGN_ACCOUNT') {
          setWhiteListStatus('valid');
        } else if (assigned === 'NIE' || assigned === 'VIRTUAL_NO_MATCH') {
          setWhiteListStatus('invalid');
        } else if (assigned === 'PROBABLE_MATCH') {
          setWhiteListStatus('error');
        } else {
          setWhiteListStatus('error');
        }
      })
      .catch(() => {
        if (!cancelled) setWhiteListStatus('error');
      });
    return () => { cancelled = true; };
  }, [open, invoiceData]);

  // Aktualizuj kwotę z rachunku VAT przy zmianie metody płatności
  useEffect(() => {
    if (!invoiceData) return;
    const gross = Number(invoiceData.gross_amount || 0);
    const net = Number(invoiceData.net_amount || 0);
    const vat = gross - net;
    if (paymentMethod === 'przelew PP') {
      setAmountFromVatAccount(vat > 0 ? vat.toFixed(2) : '0.00');
    } else {
      setAmountFromVatAccount('0.00');
    }
  }, [paymentMethod, invoiceData]);

  const grossAmount = Number(invoiceData?.gross_amount || 0);
  const netAmount = Number(invoiceData?.net_amount || 0);
  const vatAmount = grossAmount - netAmount;
  const payAmount = Number(amountToPay || 0);
  const remaining = grossAmount - payAmount;
  const overpay = payAmount > grossAmount ? payAmount - grossAmount : 0;

  const handleSettle = useCallback(async () => {
    if (!invoiceData || !settleDate) return;
    setLoading(true);
    try {
      await onSettle(invoiceData.id, {
        paid_date: settleDate,
        payment_method: paymentMethod,
        currency,
        amount_to_pay: Number(amountToPay) || 0,
        amount_vat: Number(amountVat) || 0,
        amount_from_vat_account: Number(amountFromVatAccount) || 0,
        transfer_title: transferTitle,
        unitresco_account: unitrescoAccount,
      });
      onClose();
    } catch (e) {
      console.error('Błąd rozliczania:', e);
    } finally {
      setLoading(false);
    }
  }, [invoiceData, settleDate, paymentMethod, currency, amountToPay, amountVat, amountFromVatAccount, transferTitle, unitrescoAccount, onSettle, onClose]);

  if (!invoiceData) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            {viewMode ? 'Podgląd rozliczenia' : 'Rozlicz fakturę'}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ minWidth: 30, minHeight: 30 }}>
            <Box component="img" src="/assets/close.svg" alt="zamknij" sx={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ px: 4, mt: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(_e, v) => setTabValue(v)}
            variant="standard"
            sx={{
              position: 'relative',
              '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '1px', bgcolor: '#EDEDED' },
              '& .MuiTabs-scroller': { pl: 0, ml: 0 },
              '& .MuiTabs-flexContainer': { gap: 4 },
              minHeight: 36,
            }}
            TabIndicatorProps={{ sx: { height: 2, bgcolor: '#000000' } }}
          >
            <Tab label="Informacje" disableRipple sx={{ ...tabSx, minHeight: 36 }} />
            <Tab label="Rachunki i przelew" disableRipple sx={{ ...tabSx, minHeight: 36 }} />
          </Tabs>
        </Box>

        {/* Content – fills remaining space, no scroll */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 2 }}>

            {/* ========== TAB 0: Informacje ========== */}
            {tabValue === 0 && (
              <>
                {/* 1. Dane Faktury */}
                <SectionHeader title="Dane faktury" />
                <Grid container rowSpacing={1} columnSpacing={2}>
                  <Grid item xs={6}>
                    <FieldLabel text="Data otrzymania" />
                    <TextField
                      fullWidth size="small" type="date"
                      value={invoiceData.received_date || invoiceData.created_date || ''}
                      disabled sx={readOnlyFieldSx} InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel text="Data faktury" />
                    <TextField
                      fullWidth size="small" type="date"
                      value={invoiceData.document_date || invoiceData.created_date || ''}
                      disabled sx={readOnlyFieldSx} InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel text="Wymagany termin płatności" />
                    <TextField
                      fullWidth size="small" type="date"
                      value={invoiceData.due_date || ''}
                      disabled sx={readOnlyFieldSx} InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel text="Nr faktury" />
                    <TextField
                      fullWidth size="small"
                      value={invoiceData.invoice_number || '-'}
                      disabled sx={readOnlyFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FieldLabel text="Nr KSeF" />
                    <TextField
                      fullWidth size="small"
                      value={invoiceData.ksef_number || '-'}
                      disabled sx={readOnlyFieldSx}
                    />
                  </Grid>
                </Grid>

                {/* 2. Kontrahent */}
                <Box sx={{ mt: 2 }}>
                  <SectionHeader title="Kontrahent" />
                  <Grid container rowSpacing={1} columnSpacing={2}>
                    <Grid item xs={12}>
                      <FieldLabel text="Nazwa" />
                      <TextField
                        fullWidth size="small"
                        value={invoiceData.contractor || '-'}
                        disabled sx={readOnlyFieldSx}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <FieldLabel text="Adres" />
                      <TextField
                        fullWidth size="small"
                        value={invoiceData.contractor_address || '-'}
                        disabled sx={readOnlyFieldSx}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FieldLabel text="Kod pocztowy i miejscowość" />
                      <TextField
                        fullWidth size="small"
                        value={invoiceData.contractor_postal_city || '-'}
                        disabled sx={readOnlyFieldSx}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FieldLabel text="NIP" />
                      <TextField
                        fullWidth size="small"
                        value={invoiceData.nip || '-'}
                        disabled sx={readOnlyFieldSx}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <FieldLabel text="Nr rachunku" />
                      <TextField
                        fullWidth size="small"
                        value={invoiceData.account_number || '-'}
                        disabled sx={readOnlyFieldSx}
                        InputProps={{
                          endAdornment: invoiceData.account_number ? (
                            <InputAdornment position="end">
                              <IconButton size="small" sx={{ p: 0.25 }} title="Kopiuj nr rachunku" onClick={() => { navigator.clipboard.writeText(invoiceData.account_number || ''); }}>
                                <ContentCopy sx={{ fontSize: 16, color: '#595966' }} />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FieldLabel text="Biała lista" />
                      <Box sx={{
                        height: 36,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        borderRadius: '4px',
                        border: '1px solid',
                        borderColor: whiteListStatus === 'valid' ? '#A5D6A7'
                          : whiteListStatus === 'invalid' ? '#EF9A9A'
                          : whiteListStatus === 'error' ? '#FFE082'
                          : '#d4d4d9',
                        backgroundColor: whiteListStatus === 'valid' ? '#E8F5E9'
                          : whiteListStatus === 'invalid' ? '#FFEBEE'
                          : whiteListStatus === 'error' ? '#FFF8E1'
                          : '#F5F5F8',
                      }}>
                        {whiteListStatus === 'checking' && (
                          <>
                            <CircularProgress size={16} sx={{ color: '#757575' }} />
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#757575' }}>
                              Sprawdzam...
                            </Typography>
                          </>
                        )}
                        {whiteListStatus === 'valid' && (
                          <>
                            <CheckCircle sx={{ fontSize: 18, color: '#2E7D32' }} />
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#2E7D32', fontWeight: 500 }}>
                              Na białej liście
                            </Typography>
                          </>
                        )}
                        {whiteListStatus === 'invalid' && (
                          <>
                            <Cancel sx={{ fontSize: 18, color: '#C62828' }} />
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#C62828', fontWeight: 500 }}>
                              Brak na liście
                            </Typography>
                          </>
                        )}
                        {whiteListStatus === 'error' && (
                          <>
                            <WarningAmber sx={{ fontSize: 18, color: '#F9A825' }} />
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#E65100', fontWeight: 500 }}>
                              Błąd sprawdzenia
                            </Typography>
                          </>
                        )}
                        {whiteListStatus === null && (
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#9e9e9e' }}>
                            —
                          </Typography>
                        )}
                        {whiteListStatus && whiteListStatus !== 'checking' && (
                          <Typography
                            component="a"
                            href={WL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#1565C0', textDecoration: 'none', ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25, '&:hover': { textDecoration: 'underline' } }}
                          >
                            link <OpenInNew sx={{ fontSize: 12 }} />
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

            {/* ========== TAB 1: Rachunki i przelew ========== */}
            {tabValue === 1 && (
              <>
                {/* Wartości */}
                <SectionHeader title="Wartości" />
                <Grid container rowSpacing={1} columnSpacing={2}>
                  <Grid item xs={6}>
                    <FieldLabel text="Sposób zapłaty" required={!viewMode} />
                    <Select
                      fullWidth size="small"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      disabled={viewMode}
                      sx={viewMode ? { ...selectSx, backgroundColor: '#F5F5F8' } : selectSx}
                    >
                      <MenuItem value="gotówka">Gotówka</MenuItem>
                      <MenuItem value="przelew">Przelew</MenuItem>
                      <MenuItem value="przelew PP">Przelew PP</MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={3}>
                    <FieldLabel text="Data" required={!viewMode} />
                    <TextField
                      fullWidth size="small" type="date"
                      value={settleDate}
                      onChange={(e) => setSettleDate(e.target.value)}
                      disabled={viewMode}
                      sx={viewMode ? readOnlyFieldSx : textFieldSx} InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FieldLabel text="Waluta" />
                    <Select
                      fullWidth size="small"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      disabled={viewMode}
                      sx={viewMode ? { ...selectSx, backgroundColor: '#F5F5F8' } : selectSx}
                    >
                      <MenuItem value="PLN">PLN</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </Grid>

                  <Grid item xs={6}>
                    <FieldLabel text="Pozostało do zapłaty" />
                    <TextField fullWidth size="small" value={formatMoney(grossAmount)} disabled sx={readOnlyFieldSx} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel text="w tym VAT" />
                    <TextField fullWidth size="small" value={formatMoney(vatAmount)} disabled sx={readOnlyFieldSx} />
                  </Grid>

                  <Grid item xs={6}>
                    <FieldLabel text="Kwota do spłaty" required={!viewMode} />
                    <TextField
                      fullWidth size="small" type="number"
                      value={amountToPay}
                      onChange={(e) => setAmountToPay(e.target.value)}
                      disabled={viewMode}
                      sx={viewMode ? readOnlyFieldSx : textFieldSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel text="w tym VAT" />
                    <TextField
                      fullWidth size="small" type="number"
                      value={amountVat}
                      onChange={(e) => setAmountVat(e.target.value)}
                      disabled={viewMode}
                      sx={viewMode ? readOnlyFieldSx : textFieldSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <FieldLabel text="Kwota z rachunku VAT" />
                    <TextField
                      fullWidth size="small" type="number"
                      value={amountFromVatAccount}
                      onChange={(e) => setAmountFromVatAccount(e.target.value)}
                      disabled={viewMode}
                      sx={viewMode ? readOnlyFieldSx : textFieldSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6} />

                  <Grid item xs={6}>
                    <FieldLabel text="Pozostanie do rozliczenia" />
                    <TextField
                      fullWidth size="small"
                      value={remaining > 0 ? formatMoney(remaining) : '0,00 zł'}
                      disabled
                      sx={{
                        ...readOnlyFieldSx,
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: remaining > 0 ? '#D92D20' : '#2E7D32',
                          color: remaining > 0 ? '#D92D20' : '#2E7D32',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel text="Nadpłata" />
                    <TextField
                      fullWidth size="small"
                      value={overpay > 0 ? formatMoney(overpay) : '0,00 zł'}
                      disabled
                      sx={{
                        ...readOnlyFieldSx,
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: overpay > 0 ? '#1565C0' : '#2D2D32',
                          color: overpay > 0 ? '#1565C0' : '#2D2D32',
                          fontWeight: overpay > 0 ? 600 : 400,
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Rachunki */}
                <Box sx={{ mt: 2 }}>
                  <SectionHeader title="Rachunki" />
                <Grid container rowSpacing={1} columnSpacing={2}>
                  <Grid item xs={12}>
                    <FieldLabel text="Rachunek Unitresco" />
                    <Autocomplete
                      freeSolo
                      fullWidth
                      size="small"
                      disabled={viewMode}
                      options={companyBankAccounts}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return formatAccountNumber(option.account_number);
                      }}
                      inputValue={unitrescoAccount}
                      onInputChange={(_, newValue, reason) => {
                        if (reason === 'input' || reason === 'clear') {
                          setUnitrescoAccount(newValue);
                        }
                      }}
                      onChange={(_, newValue) => {
                        if (typeof newValue === 'string') {
                          setUnitrescoAccount(newValue);
                        } else if (newValue) {
                          setUnitrescoAccount(formatAccountNumber(newValue.account_number));
                        } else {
                          setUnitrescoAccount('');
                        }
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={typeof option === 'string' ? option : option.id}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontFamily: 'Roboto Mono, monospace', fontSize: '12px' }}>
                                {typeof option === 'string' ? option : formatAccountNumber(option.account_number)}
                              </Typography>
                              {typeof option !== 'string' && option.is_primary ? (
                                <Box component="span" sx={{
                                  display: 'inline-block', px: 0.6, py: 0.1,
                                  bgcolor: '#E8F5E9', color: '#2E7D32', borderRadius: '3px',
                                  fontSize: '9px', fontWeight: 600, fontFamily: 'Roboto, sans-serif',
                                  lineHeight: 1.3,
                                }}>
                                  GŁÓWNY
                                </Box>
                              ) : null}
                            </Box>
                            {typeof option !== 'string' && option.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Nr rachunku Unitresco" sx={textFieldSx} />
                      )}
                      ListboxProps={{ sx: { '& .MuiAutocomplete-option': { py: 0.5 } } }}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <FieldLabel text="Rachunek podmiotu" />
                    <TextField
                      fullWidth size="small"
                      value={invoiceData.account_number || '-'}
                      disabled sx={readOnlyFieldSx}
                      InputProps={{
                        endAdornment: invoiceData.account_number ? (
                          <InputAdornment position="end">
                            <IconButton size="small" sx={{ p: 0.25 }} title="Kopiuj nr rachunku" onClick={() => { navigator.clipboard.writeText(invoiceData.account_number || ''); }}>
                              <ContentCopy sx={{ fontSize: 16, color: '#595966' }} />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FieldLabel text="Biała lista" />
                    <Box sx={{
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.5,
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: whiteListStatus === 'valid' ? '#A5D6A7'
                        : whiteListStatus === 'invalid' ? '#EF9A9A'
                        : whiteListStatus === 'error' ? '#FFE082'
                        : '#d4d4d9',
                      backgroundColor: whiteListStatus === 'valid' ? '#E8F5E9'
                        : whiteListStatus === 'invalid' ? '#FFEBEE'
                        : whiteListStatus === 'error' ? '#FFF8E1'
                        : '#F5F5F8',
                    }}>
                      {whiteListStatus === 'checking' && (
                        <>
                          <CircularProgress size={16} sx={{ color: '#757575' }} />
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#757575' }}>
                            Sprawdzam...
                          </Typography>
                        </>
                      )}
                      {whiteListStatus === 'valid' && (
                        <>
                          <CheckCircle sx={{ fontSize: 18, color: '#2E7D32' }} />
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#2E7D32', fontWeight: 500 }}>
                            Na białej liście
                          </Typography>
                        </>
                      )}
                      {whiteListStatus === 'invalid' && (
                        <>
                          <Cancel sx={{ fontSize: 18, color: '#C62828' }} />
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#C62828', fontWeight: 500 }}>
                            Brak na liście
                          </Typography>
                        </>
                      )}
                      {whiteListStatus === 'error' && (
                        <>
                          <WarningAmber sx={{ fontSize: 18, color: '#F9A825' }} />
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#E65100', fontWeight: 500 }}>
                            Błąd sprawdzenia
                          </Typography>
                        </>
                      )}
                      {whiteListStatus === null && (
                        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#9e9e9e' }}>
                          —
                        </Typography>
                      )}
                      {whiteListStatus && whiteListStatus !== 'checking' && (
                        <Typography
                          component="a"
                          href={WL_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#1565C0', textDecoration: 'none', ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25, '&:hover': { textDecoration: 'underline' } }}
                        >
                          link <OpenInNew sx={{ fontSize: 12 }} />
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <SectionHeader title="Przelew" />
                  <Grid container rowSpacing={1} columnSpacing={2}>
                    <Grid item xs={12}>
                      <FieldLabel text="Tytuł przelewu" />
                      <TextField
                        fullWidth size="small"
                        value={transferTitle}
                        onChange={(e) => setTransferTitle(e.target.value)}
                        disabled={viewMode}
                        sx={viewMode ? readOnlyFieldSx : textFieldSx}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

          </Box>
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', alignItems: 'center', justifyContent: viewMode ? 'space-between' : 'flex-end', gap: 2 }}>
          {viewMode ? (
            <>
              <Button
                variant="outlined"
                disabled={loading}
                onClick={async () => {
                  if (!invoiceData || !onUnsettle) return;
                  setLoading(true);
                  try {
                    await onUnsettle(invoiceData.id);
                    onClose();
                  } catch (e) {
                    console.error('Błąd cofania rozliczenia:', e);
                  } finally {
                    setLoading(false);
                  }
                }}
                sx={{
                  minWidth: 160,
                  height: 40,
                  borderRadius: '4px',
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 600,
                  fontSize: 14,
                  borderColor: '#D92D20',
                  color: '#D92D20',
                  '&:hover': { borderColor: '#B42318', color: '#B42318', backgroundColor: 'rgba(217,45,32,0.04)' },
                }}
              >
                {loading ? 'Cofanie...' : 'Cofnij rozliczenie'}
              </Button>
              <Button
                variant="text"
                onClick={onClose}
                sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}
              >
                Zamknij
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="text"
                onClick={onClose}
                sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}
              >
                Anuluj
              </Button>
              <Button
                variant="contained"
                disabled={loading || !settleDate}
                onClick={handleSettle}
                sx={{
                  minWidth: 140,
                  height: 40,
                  borderRadius: '4px',
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 600,
                  fontSize: 14,
                  backgroundColor: '#020242',
                  '&:hover': { backgroundColor: '#020242' },
                }}
              >
                {loading ? 'Rozliczanie...' : 'Rozlicz fakturę'}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SettleInvoiceDrawer;
